import React from "react";

export const STUDY_ROLE_META = {
    core: { label: "Core", hint: "Primary development in this indication" },
    clinpharm: { label: "Clin-pharm", hint: "Program-level Phase 1 / clinical pharmacology" },
    label_expansion: { label: "Label expansion", hint: "New indication / population for an approved compound" },
};

export const TIMING_META = {
    window_open_now: { label: "Window open now", short: "Open now", tone: "border-emerald-400/40 text-emerald-300 bg-emerald-400/10" },
    opening_within_12m: { label: "Opening ≤ 12 mo", short: "≤ 12 mo", tone: "border-teal-400/40 text-teal-300 bg-teal-400/10" },
    under_regulatory_review: { label: "Under regulatory review", short: "Reg. review", tone: "border-violet-400/40 text-violet-300 bg-violet-400/10" },
    overdue: { label: "Overdue", short: "Overdue", tone: "border-amber-400/40 text-amber-300 bg-amber-400/10" },
    frontier_running_long: { label: "Frontier running long", short: "Running long", tone: "border-rose-400/40 text-rose-300 bg-rose-400/10" },
};

export const CRO_META = {
    confirmed: { label: "CRO confirmed" },
    likely: { label: "CRO likely" },
    confirmed_or_likely: { label: "Confirmed or likely" },
    none: { label: "No CRO" },
};

const ORDER = {
    study_role: ["core", "clinpharm", "label_expansion"],
    timing: ["window_open_now", "opening_within_12m", "under_regulatory_review", "overdue", "frontier_running_long"],
    cro_status: ["confirmed", "likely", "confirmed_or_likely", "none"],
};
const META = { study_role: STUDY_ROLE_META, timing: TIMING_META, cro_status: CRO_META };

/** Multi-select chip group driven by a facet bucket list `[{value, count}]`.
 *  Counts come from POST /facets/query, which counts each family WITHOUT its
 *  own selection — so a chip's count is what the list would show if you
 *  toggled it on. */
export function ChipGroup({ family, title, buckets, selected, onChange, hint }) {
    const counts = Object.fromEntries((buckets || []).map((b) => [b.value, b.count]));
    const values = ORDER[family] || Object.keys(counts);
    const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    return (
        <div data-testid={`chip-group-${family}`}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-400">{title}</span>
                {selected.length ? (
                    <button
                        type="button"
                        onClick={() => onChange([])}
                        className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300"
                        data-testid={`chip-clear-${family}`}
                    >
                        clear
                    </button>
                ) : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
                {values.map((v) => {
                    const on = selected.includes(v);
                    const n = counts[v] ?? 0;
                    const meta = (META[family] || {})[v] || { label: v };
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => toggle(v)}
                            title={meta.hint || meta.label}
                            aria-pressed={on}
                            disabled={!on && n === 0}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                                on
                                    ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-200"
                                    : n === 0
                                        ? "border-slate-800 text-slate-600 cursor-not-allowed"
                                        : "border-slate-700 text-slate-300 hover:border-slate-500 hover:text-slate-100"
                            }`}
                            data-testid={`chip-${family}-${v}`}
                        >
                            {meta.label}
                            <span className={`font-mono ${on ? "text-emerald-300" : "text-slate-500"}`} data-testid={`chip-count-${family}-${v}`}>
                                {n}
                            </span>
                        </button>
                    );
                })}
            </div>
            {hint ? <div className="text-[10px] text-slate-600 mt-1">{hint}</div> : null}
        </div>
    );
}

/** Compact per-row badges: study role, timing buckets, CRO. */
export function TrackSignalBadges({ track }) {
    const role = track.study_role;
    const flags = track.timing_flags || [];
    const cro = track.cro;
    return (
        <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`track-badges-${track.track_id}`}>
            {role && role !== "core" ? (
                <span className="rounded border border-sky-400/40 bg-sky-400/10 text-sky-300 px-1.5 py-0.5 text-[10px]" data-testid={`badge-role-${track.track_id}`}>
                    {STUDY_ROLE_META[role]?.label || role}
                </span>
            ) : null}
            {flags.map((f) => (
                <span key={f} className={`rounded border px-1.5 py-0.5 text-[10px] ${TIMING_META[f]?.tone || "border-slate-600 text-slate-400"}`} data-testid={`badge-timing-${f}-${track.track_id}`}>
                    {TIMING_META[f]?.short || f}
                </span>
            ))}
            {cro?.name ? (
                <span className="rounded border border-slate-500/40 text-slate-300 px-1.5 py-0.5 text-[10px]" data-testid={`badge-cro-${track.track_id}`}>
                    CRO {cro.name}{cro.conf === "likely" ? " (likely)" : ""}
                </span>
            ) : null}
            {track.program_track_count > 1 ? (
                <span className="rounded border border-slate-700 text-slate-500 px-1.5 py-0.5 text-[10px]" title="Indication tracks of this compound" data-testid={`badge-program-count-${track.track_id}`}>
                    {track.program_track_count} tracks
                </span>
            ) : null}
        </div>
    );
}
